import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    HiOutlineArrowLeft,
    HiOutlineCheck,
    HiOutlineClock,
    HiOutlineDocumentText,
    HiOutlinePhotograph,
    HiOutlineChatAlt2
} from "react-icons/hi";

const CampaignDetails = () => {
    const { id } = useParams();

    // Mock Campaign Data (In real app, fetch using ID)
    const [campaign, setCampaign] = useState({
        id: id,
        title: "Summer Collection Launch",
        brand: "Fashion Co",
        budget: "$5,000",
        deadline: "Oct 24, 2024",
        status: "Applications", // Initial status
        description: "We are looking for fashion influencers to promote our new summer line. Focus on vibrant colors and outdoor settings.",
        deliverables: "1 Reel, 2 Stories, 1 Feed Post",
        creator: {
            name: "Sarah Styles",
            handle: "@sarah.styles"
        }
    });

    // Workflow Stages
    const stages = [
        { id: "Applications", label: "Applications", icon: <HiOutlineDocumentText /> },
        { id: "In Progress", label: "In Progress", icon: <HiOutlineClock /> },
        { id: "Content Submitted", label: "Review", icon: <HiOutlinePhotograph /> },
        { id: "Completed", label: "Completed", icon: <HiOutlineCheck /> }
    ];

    const currentStageIndex = stages.findIndex(s => s.id === campaign.status);

    const handleAction = (action) => {
        // Simple state machine for demonstration
        if (action === "Accept Application") setCampaign({ ...campaign, status: "In Progress" });
        if (action === "Submit Content") setCampaign({ ...campaign, status: "Content Submitted" }); // Simulate Creator action
        if (action === "Approve Content") setCampaign({ ...campaign, status: "Completed" });
        if (action === "Reject Content") setCampaign({ ...campaign, status: "In Progress" });
    };

    return (
        <div className="min-h-screen bg-[#FDFBF7] p-6">
            {/* Header */}
            <div className="max-w-4xl mx-auto mb-8">
                <Link to="/brand/campaigns" className="inline-flex items-center text-gray-500 hover:text-gray-900 mb-4 transition-colors">
                    <HiOutlineArrowLeft className="mr-2" />
                    Back to Campaigns
                </Link>
                <div className="flex justify-between items-start">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">{campaign.title}</h1>
                        <p className="text-gray-500">Campaign ID: #{campaign.id} • {campaign.deadline}</p>
                    </div>
                    <div className="flex justify-between items-center gap-4">
                        <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gray-50">
                            <HiOutlineChatAlt2 />
                            Message
                        </button>
                    </div>
                </div>
            </div>

            {/* Stepper */}
            <div className="max-w-4xl mx-auto mb-10">
                <div className="relative flex justify-between">
                    {/* Connecting Line */}
                    <div className="absolute top-1/2 left-0 w-full h-1 bg-gray-200 -translate-y-1/2 z-0"></div>

                    {stages.map((stage, index) => {
                        const isCompleted = index < currentStageIndex;
                        const isCurrent = index === currentStageIndex;

                        return (
                            <div key={stage.id} className="relative z-10 flex flex-col items-center bg-[#FDFBF7] px-4">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors duration-300
                                    ${isCompleted || isCurrent
                                        ? 'bg-[#0044CC] border-[#0044CC] text-white'
                                        : 'bg-white border-gray-300 text-gray-400'
                                    }`}>
                                    {isCompleted ? <HiOutlineCheck /> : stage.icon}
                                </div>
                                <span className={`mt-2 text-sm font-medium ${isCurrent ? 'text-[#0044CC]' : 'text-gray-500'}`}>
                                    {stage.label}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Main Content Area */}
            <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">

                {/* Left Column: Actions & Status */}
                <div className="md:col-span-2 space-y-6">
                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Current Action</h2>

                        {/* Dynamic Action Area based on Status */}
                        {campaign.status === "Applications" && (
                            <div className="text-center py-8">
                                <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-[#0044CC] text-2xl">
                                    <HiOutlineDocumentText />
                                </div>
                                <h3 className="text-lg font-bold mb-2">Review Applications</h3>
                                <p className="text-gray-500 mb-6">You have pending applications for this campaign.</p>
                                <button
                                    onClick={() => handleAction("Accept Application")}
                                    className="px-6 py-3 bg-[#0044CC] text-white rounded-xl font-bold hover:bg-blue-700 transition-colors"
                                >
                                    Accept Application (Simulate)
                                </button>
                            </div>
                        )}

                        {campaign.status === "In Progress" && (
                            <div className="text-center py-8">
                                <div className="bg-yellow-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-yellow-600 text-2xl">
                                    <HiOutlineClock />
                                </div>
                                <h3 className="text-lg font-bold mb-2">Awaiting Content</h3>
                                <p className="text-gray-500 mb-6">The creator is working on the deliverables.</p>
                                <button
                                    onClick={() => handleAction("Submit Content")}
                                    className="px-6 py-3 border-2 border-dashed border-gray-300 text-gray-500 rounded-xl font-bold hover:bg-gray-50 transition-colors"
                                >
                                    Simulate Creator Submitting Content
                                </button>
                            </div>
                        )}

                        {campaign.status === "Content Submitted" && (
                            <div className="text-center py-8">
                                <div className="bg-purple-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-purple-600 text-2xl">
                                    <HiOutlinePhotograph />
                                </div>
                                <h3 className="text-lg font-bold mb-2">Content for Review</h3>
                                <p className="text-gray-500 mb-6">Please review the content submitted by the creator.</p>
                                <div className="flex gap-4 justify-center">
                                    <button
                                        onClick={() => handleAction("Reject Content")}
                                        className="px-6 py-3 bg-red-50 text-red-600 rounded-xl font-bold hover:bg-red-100 transition-colors"
                                    >
                                        Request Changes
                                    </button>
                                    <button
                                        onClick={() => handleAction("Approve Content")}
                                        className="px-6 py-3 bg-[#0044CC] text-white rounded-xl font-bold hover:bg-blue-700 transition-colors"
                                    >
                                        Approve Content
                                    </button>
                                </div>
                            </div>
                        )}

                        {campaign.status === "Completed" && (
                            <div className="text-center py-8">
                                <div className="bg-green-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-green-600 text-2xl">
                                    <HiOutlineCheck />
                                </div>
                                <h3 className="text-lg font-bold mb-2">Campaign Completed!</h3>
                                <p className="text-gray-500">All deliverables have been approved.</p>
                            </div>
                        )}
                    </div>

                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <h2 className="text-lg font-bold text-gray-900 mb-4">Brief & Deliverables</h2>
                        <div className="space-y-4">
                            <div>
                                <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Description</h4>
                                <p className="text-gray-600 mt-1">{campaign.description}</p>
                            </div>
                            <div>
                                <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Required Deliverables</h4>
                                <p className="text-gray-600 mt-1">{campaign.deliverables}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column: Key Details */}
                <div className="space-y-6">
                    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                        <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">Campaign Details</h3>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center py-2 border-b border-gray-50">
                                <span className="text-gray-600">Budget</span>
                                <span className="font-bold text-gray-900">{campaign.budget}</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-gray-50">
                                <span className="text-gray-600">Deadline</span>
                                <span className="font-bold text-gray-900">{campaign.deadline}</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-gray-50">
                                <span className="text-gray-600">Creator</span>
                                <span className="font-bold text-[#0044CC]">{campaign.creator.name}</span>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default CampaignDetails;
